import {
  Amount,
  Box,
  Button,
  EditIcon,
  ExternalLinkIcon,
  Heading,
  Link,
  SparklesIcon,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  TrashIcon,
} from '@razorpay/blade/components';
import React from 'react';
import ComponentContainer from '../components/generics/ComponentContainer';
import ComponentHeader from '../components/generics/ComponentHeader';
import ComponentSection from '../components/generics/ComponentSection';
import ComponentSectionItem from '../components/generics/ComponentSectionItem';
import useHasPermission from 'utils/useHasPermission';
import permissions from 'constants/permissions';
import { getStaticMediaUrl } from 'utils/Urls';
import noComponents from '../../../assets/noComponents.svg';
import { paiseAmountToRupee } from 'utils/Numbers';
import { formatPayrollMonth } from 'components/Bonus/components/AllocateBonusModal/utils';
import { PerquisiteTaxDeductionBehaviour } from 'api/gql-utils/perquisitesComponents/types';
import { PRO_RATED_TDS_CAPTION, TAX_DEDUCTION_BEHAVIOUR, TDS_DEDUCTION_LABEL } from '../constants';

type PerquisiteData = {
  id: string;
  component: string;
  amountPaidByEmployee: number;
  totalValue: number;
  payrollMonth?: string;
  taxDeductionBehaviour?: PerquisiteTaxDeductionBehaviour;
};

const PerquisitesBreakdown = ({
  openPerquisiteModal,
  openDeleteModal,
  setSelectedComponentId,
  perquisitesData,
  isLoading,
  showTdsDeduction = false,
}: {
  openPerquisiteModal: () => void;
  openDeleteModal: () => void;
  setSelectedComponentId: (id: string) => void;
  perquisitesData: PerquisiteData[];
  isLoading: boolean;
  showTdsDeduction?: boolean;
}) => {
  const hasPayrollEditPermission = useHasPermission([permissions.PAYROLL_EDIT]);
  const onAddNewPerquisite = () => {
    setSelectedComponentId('');
    openPerquisiteModal();
  };

  const onEdit = (id: string) => {
    // TODO: Get the perquisite form data from the API
    setSelectedComponentId(id);
    openPerquisiteModal();
  };

  const onDelete = (id: string) => {
    setSelectedComponentId(id);
    openDeleteModal();
  };

  return (
    <>
      <ComponentContainer>
        <ComponentSection>
          <ComponentHeader
            icon={<SparklesIcon size="medium" color="surface.icon.primary.normal" />}
            iconBackground={'surface.background.primary.subtle'}
            title={'Perquisites'}
            description={'Perks that employees enjoy over and above their salary'}
            button={
              hasPayrollEditPermission && perquisitesData.length !== 0 ? (
                <Button
                  variant="tertiary"
                  color="white"
                  marginLeft={'auto'}
                  onClick={onAddNewPerquisite}>
                  + New Perquisite
                </Button>
              ) : null
            }
          />
          <ComponentSectionItem>
            <Box borderRadius={'large'} overflow={'hidden'}>
              {perquisitesData.length === 0 && !isLoading ? (
                <ComponentSectionItem>
                  <Box
                    display="flex"
                    flexDirection={'row'}
                    gap="spacing.8"
                    height="100%"
                    justifyContent={'center'}
                    alignItems={'center'}>
                    <img src={getStaticMediaUrl(noComponents)} alt="no-recurring-deductions" />
                    <Box display={'flex'} flexDirection={'column'} gap={'spacing.7'}>
                      <Heading weight="semibold">No perquisites added</Heading>
                      <Box
                        display={'flex'}
                        flexDirection={'row'}
                        gap={'spacing.5'}
                        alignItems={'center'}
                        justifyContent={'center'}>
                        {hasPayrollEditPermission ? (
                          <Button
                            onClick={() => {
                              onAddNewPerquisite();
                            }}>
                            + Add Perquisite
                          </Button>
                        ) : null}
                      </Box>
                    </Box>
                  </Box>
                </ComponentSectionItem>
              ) : isLoading ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  gap="spacing.5"
                  height="100%">
                  <Spinner accessibilityLabel="loading perquisites" />
                  <Text>Loading...</Text>
                </Box>
              ) : (
                <Table
                  data={{
                    nodes: perquisitesData,
                  }}
                  backgroundColor="transparent">
                  {(data) => (
                    <>
                      <TableHeader>
                        <TableHeaderRow>
                          <TableHeaderCell>
                            <Text weight="semibold">Component</Text>
                          </TableHeaderCell>
                          {showTdsDeduction ? (
                            <TableHeaderCell>
                              <Text weight="semibold">TDS Deduction</Text>
                            </TableHeaderCell>
                          ) : null}
                          <TableHeaderCell textAlign="right">
                            {showTdsDeduction ? 'Total Value' : 'Total Value (Annual)'}
                          </TableHeaderCell>
                          <TableHeaderCell textAlign="right">
                            {showTdsDeduction ? 'Paid by Employee' : 'Amount Paid by Employee'}
                          </TableHeaderCell>
                          {hasPayrollEditPermission ? (
                            <TableHeaderCell textAlign="right">
                              <Text weight="semibold">Action</Text>
                            </TableHeaderCell>
                          ) : null}
                        </TableHeaderRow>
                      </TableHeader>
                      <TableBody>
                        {data.map((row) => (
                          <TableRow key={row.id} item={row}>
                            <TableCell>
                              <Text>{row.component}</Text>
                            </TableCell>
                            {showTdsDeduction ? (
                              <TableCell>
                                <Box display="flex" flexDirection="column">
                                  <Text>
                                    {row.taxDeductionBehaviour
                                      ? TDS_DEDUCTION_LABEL[row.taxDeductionBehaviour]
                                      : '-'}
                                  </Text>
                                  {row.taxDeductionBehaviour ? (
                                    <Text
                                      variant="caption"
                                      size="small"
                                      color="surface.text.gray.muted">
                                      {row.taxDeductionBehaviour === TAX_DEDUCTION_BEHAVIOUR.INSTANT
                                        ? formatPayrollMonth(row.payrollMonth) || '-'
                                        : PRO_RATED_TDS_CAPTION}
                                    </Text>
                                  ) : null}
                                </Box>
                              </TableCell>
                            ) : null}
                            <TableCell textAlign="right">
                              <Amount value={paiseAmountToRupee(row.totalValue)} />
                            </TableCell>
                            <TableCell textAlign="right">
                              <Amount value={paiseAmountToRupee(row.amountPaidByEmployee)} />
                            </TableCell>

                            {hasPayrollEditPermission ? (
                              <TableCell textAlign="right">
                                <Box display="flex" gap="spacing.5">
                                  <Link
                                    icon={EditIcon}
                                    onClick={() => onEdit(row.id)}
                                    testID={`edit-${row.id}`}
                                  />
                                  <Link
                                    icon={TrashIcon}
                                    onClick={() => onDelete(row.id)}
                                    testID={`delete-${row.id}`}
                                  />
                                </Box>
                              </TableCell>
                            ) : null}
                          </TableRow>
                        ))}
                      </TableBody>
                    </>
                  )}
                </Table>
              )}
            </Box>
          </ComponentSectionItem>
        </ComponentSection>
      </ComponentContainer>
    </>
  );
};

export default PerquisitesBreakdown;
